import React, { useEffect, useState } from "react";
import { Form, Modal, Placeholder } from "react-bootstrap";

import DstButton from "../../../../components/DstButton/DstButton";
import DstConfirm from "../../../../components/DstConfirm/DstConfirm";
import DstTable from "../../../../components/DstTable/DstTable";
import DstTableSearch from "../../../../components/DstTable/DstTableSearch/DstTableSearch";
import Popup from "../../../../components/Popup/Popup";
import { useSession } from "../../../../contexts/SessionContext";
import { PermissionComponent, validateUserPermission } from "../../../../services/PermissionGuard";
import { getAgeHumanly } from "../../../../services/TimeService";
import translate from "../../../../services/Translate";
import { createResource, deleteResource, editResource, fetchResources } from "./Resources.function";

const Resource = ({ meetingId, language }: { meetingId: string; language: string }) => {
  const { permissions } = useSession();
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [meetingResources, setMeetingResources] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [resourceName, setResourceName] = useState("");
  const [resourceUrl, setResourceUrl] = useState("");
  const [resourceIsPublic, setResourceIsPublic] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentResource, setCurrentResource] = useState<any>(null);

  const columns = [
    { key: "name", name: translate(language, "PAGES.MEETING.RESOURCES.NAME") },
    {
      key: "dt_added",
      name: translate(language, "PAGES.MEETING.RESOURCES.CREATION_DATE"),
      render: (meetingResource: any) => getAgeHumanly(language, new Date(meetingResource.dt_added).getTime()),
    },
    {
      key: "dt_updated",
      name: translate(language, "PAGES.MEETING.RESOURCES.UPDATE_DATE"),
      render: (meetingResource: any) =>
        meetingResource.dt_updated ? getAgeHumanly(language, new Date(meetingResource.dt_updated).getTime()) : "N/A",
    },
    {
      key: "url",
      name: translate(language, "PAGES.MEETING.RESOURCES.LINK"),
      render: (meetingResource: any) => (
        <span className="d-flex align-items-center">
          {meetingResource.url}{" "}
          <a
            className="ms-2 text-decoration-none"
            href={meetingResource.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
          >
            <span className="dst-icon-link-external-02" />
          </a>
        </span>
      ),
    },
    {
      key: "author",
      name: translate(language, "PAGES.MEETING.RESOURCES.OWNER"),
      render: (meetingResource: any) => meetingResource.author?.email || "N/A",
    },
    {
      key: "is_public",
      name: translate(language, "PAGES.MEETING.RESOURCES.PUBLIC"),
      render: (meetingResource: any) =>
        meetingResource.is_public ? (
          <span className="dst-icon-check green-font m-regular" />
        ) : (
          <span className="dst-icon-x dark-red-font m-regular" />
        ),
    },
    {
      key: "action",
      name: translate(language, "PAGES.MEETING.RESOURCES.ACTIONS.TITLE"),
      render: (meetingResource: any) => (
        <PermissionComponent neededPermission={[{ methods: ["DELETE"], route: "/meetings/*/resources/*" }]}>
          <DstButton
            btnClass="delete-button d-flex align-items-center"
            value={translate(language, "PAGES.MEETING.RESOURCES.ACTIONS.DELETE")}
            variant="danger"
            btnSize="sm"
            btnImageBefore={<span className="dst-icon-trash-01 pe-1" />}
            clickFunction={() => {
              setShowDeleteModal(true);
              setCurrentResource(meetingResource);
            }}
          />
        </PermissionComponent>
      ),
    },
  ];

  useEffect(() => {
    fetchResources(meetingId, setMeetingResources, language, () => setLoading(false));
  }, [meetingId, language]);

  const _handleConfirmClick = () => {
    if (
      resourceIsPublic &&
      !window.confirm(translate(language, "PAGES.MEETING.RESOURCES.MODAL.PUBLIC_RESOURCE_WARNING"))
    ) {
      return;
    }

    if (isEditing && currentResource) {
      editResource(
        meetingId,
        currentResource.id,
        language,
        setSubmitLoading,
        resourceName,
        resourceUrl,
        resourceIsPublic,
        () => {
          setLoading(true);
          fetchResources(meetingId, setMeetingResources, language, () => setLoading(false));
          setShowModal(false);
        }
      );
    } else {
      createResource(meetingId, language, setSubmitLoading, resourceName, resourceUrl, resourceIsPublic, () => {
        setLoading(true);
        fetchResources(meetingId, setMeetingResources, language, () => setLoading(false));
        setShowModal(false);
      });
    }
  };

  if (loading) {
    return (
      <Placeholder animation="glow">
        <div className="mx-3">
          <div className="d-flex justify-content-between mb-3">
            <div className="d-flex">
              <Placeholder className="me-3 align-content-center mb-0" as="h2" xs={12} md={6} />
              <Placeholder as="input" xs={12} md={4} />
            </div>
            <DstButton mock btnWidth="100px" />
          </div>
        </div>
      </Placeholder>
    );
  }

  return (
    <React.Fragment>
      <div className="mx-3">
        <div className="d-flex justify-content-between mb-3">
          <div className="d-flex">
            <h2 className="me-3 align-content-center mb-0">{translate(language, "PAGES.MEETING.RESOURCES.TITLE")}</h2>
            <DstTableSearch
              data={meetingResources}
              searchableColumns={["name", "author", "url"]}
              onSearch={setFilteredData}
            />
          </div>
          <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/meetings/*/resources" }]}>
            <DstButton
              btnImageBefore={<span className="dst-icon-plus me-2 align-text-bottom" />}
              value={translate(language, "PAGES.MEETING.RESOURCES.BUTTON_ADD_RESOURCES")}
              clickFunction={() => {
                setIsEditing(false);
                setResourceName("");
                setResourceUrl("");
                setResourceIsPublic(false);
                setShowModal(true);
              }}
            />
          </PermissionComponent>
        </div>
        <DstTable
          classTable="vertical-align-middle"
          data={filteredData}
          columns={columns}
          onRowClick={(event, meetingResource: any) => {
            validateUserPermission([{ methods: ["GET"], route: "/meetings/*/resources/*" }], permissions) &&
              !(event.target as HTMLElement).closest(".delete-button") &&
              (() => {
                setIsEditing(true);
                setCurrentResource({
                  ...meetingResource,
                });
                setResourceName(meetingResource.name);
                setResourceUrl(meetingResource.url);
                setResourceIsPublic(meetingResource.is_public);
                setShowModal(true);
              })();
          }}
        />
      </div>
      <DstConfirm
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={() =>
          deleteResource(meetingId, currentResource.id, language, () => {
            setShowDeleteModal(false);
            setLoading(true);
            fetchResources(meetingId, setMeetingResources, language, () => setLoading(false));
          })
        }
        title={translate(language, "PAGES.MEETING.RESOURCES.MODAL_DELETION.CONFIRM_DELETE_TITLE")}
        messageConfirm={translate(language, "PAGES.MEETING.RESOURCES.MODAL_DELETION.CONFIRM_DELETE_TEXT")}
        confirmTextButton={translate(language, "PAGES.MEETING.RESOURCES.MODAL_DELETION.CONFIRM_DELETE_BTN")}
      />
      <Popup
        show={showModal}
        onClose={() => setShowModal(false)}
        modalTitle={
          <div className="blue-font">
            <span className="dst-icon-file-05" />{" "}
            {translate(
              language,
              isEditing ? "PAGES.MEETING.RESOURCES.MODAL.EDIT_TITLE" : "PAGES.MEETING.RESOURCES.MODAL.CREATE_TITLE"
            )}
          </div>
        }
        scrollable
      >
        <Form>
          <Form.Group>
            <Form.Label>{translate(language, "PAGES.MEETING.RESOURCES.MODAL.RESOURCES_LABEL_NAME")}</Form.Label>
            <Form.Control
              type="text"
              value={resourceName}
              onChange={(event) => setResourceName(event.target.value)}
              placeholder={translate(language, "PAGES.MEETING.RESOURCES.MODAL.RESOURCES_PLACEHOLDER_NAME")}
            />
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>{translate(language, "PAGES.MEETING.RESOURCES.MODAL.RESOURCES_LABEL_FILE_LINK")}</Form.Label>
            <Form.Control
              type="text"
              value={resourceUrl}
              onChange={(event) => setResourceUrl(event.target.value)}
              placeholder={translate(language, "PAGES.MEETING.RESOURCES.MODAL.RESOURCES_PLACEHOLDER_FILE_LINK")}
            />
          </Form.Group>
          <Form.Check
            className="mt-4"
            type="checkbox"
            id="meeting-resource-public-input"
            label={
              <label htmlFor="meeting-resource-public-input">
                {translate(language, "PAGES.MEETING.RESOURCES.MODAL.RESOURCES_IS_PUBLIC_CHECK")}
              </label>
            }
            checked={resourceIsPublic}
            onChange={(event) => setResourceIsPublic(event.target.checked)}
          />
        </Form>
        <Modal.Footer className="pt-3 border-top-0">
          <DstButton
            variant="secondary"
            value={translate(language, "PAGES.MEETING.RESOURCES.MODAL.BUTTON_CANCEL")}
            clickFunction={() => setShowModal(false)}
          />
          <DstButton
            value={translate(language, "PAGES.MEETING.RESOURCES.MODAL.BUTTON_CONFIRM")}
            loading={submitLoading}
            clickFunction={_handleConfirmClick}
          />
        </Modal.Footer>
      </Popup>
    </React.Fragment>
  );
};

export default Resource;
